import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getPortalOrderFormData } from "@/lib/portal.functions";
import { getPortalBranding, updatePortalBranding } from "@/lib/portal-branding.functions";
import {
  listReportSubscriptions, createReportSubscription,
  updateReportSubscription, deleteReportSubscription,
} from "@/lib/portal-subscriptions.functions";
import { runServerFn } from "@/lib/server-fn";
import { PortalPageHeader } from "@/components/portal-shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2, Save, Plus, Mail } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/settings")({ component: PortalSettings });

const REPORTS = [
  { key: "inventory_snapshot", label: "Inventory snapshot" },
  { key: "orders_summary", label: "Orders summary" },
  { key: "inbound_summary", label: "Inbound summary" },
  { key: "sla_summary", label: "SLA summary" },
  { key: "invoice_summary", label: "Invoice summary" },
] as const;
const CADENCES = ["daily","weekly","monthly"] as const;

function PortalSettings() {
  const qc = useQueryClient();
  const fetchForm = useServerFn(getPortalOrderFormData);
  const getBranding = useServerFn(getPortalBranding);
  const updateBranding = useServerFn(updatePortalBranding);
  const listSubs = useServerFn(listReportSubscriptions);
  const createSub = useServerFn(createReportSubscription);
  const updateSub = useServerFn(updateReportSubscription);
  const delSub = useServerFn(deleteReportSubscription);

  const { data: form } = useQuery({
    queryKey: ["portal-form"], queryFn: () => runServerFn(() => fetchForm(), "Loading"),
  });
  const customerId = form?.customer?.id;
  const tenantId = form?.customer?.tenant_id;

  const { data: brandingRes } = useQuery({
    queryKey: ["portal-branding", customerId],
    enabled: !!customerId,
    queryFn: () => getBranding({ data: { customerId: customerId! } }),
  });
  const branding = brandingRes?.branding;

  const [logoUrl, setLogoUrl] = useState("");
  const [color, setColor] = useState("#6366f1");
  const [supportEmail, setSupportEmail] = useState("");
  const [welcome, setWelcome] = useState("");
  useEffect(() => {
    if (!branding) return;
    setLogoUrl(branding.logo_url ?? "");
    setColor(branding.primary_color_hex ?? "#6366f1");
    setSupportEmail(branding.support_email ?? "");
    setWelcome(branding.welcome_message ?? "");
  }, [branding]);

  const saveBranding = useMutation({
    mutationFn: () => updateBranding({ data: {
      customerId: customerId!,
      logoUrl: logoUrl.trim() || null,
      primaryColorHex: /^#[0-9A-Fa-f]{6}$/.test(color) ? color : null,
      supportEmail: supportEmail.trim() || null,
      welcomeMessage: welcome.trim() || null,
    } }),
    onSuccess: () => { toast.success("Branding saved"); qc.invalidateQueries({ queryKey: ["portal-branding"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  // Subscriptions
  const { data: subs } = useQuery({
    queryKey: ["portal-subs", customerId],
    enabled: !!customerId,
    queryFn: () => listSubs({ data: { customerId: customerId! } }),
  });
  const [newReport, setNewReport] = useState<typeof REPORTS[number]["key"]>("inventory_snapshot");
  const [newCadence, setNewCadence] = useState<typeof CADENCES[number]>("weekly");
  const [newRecipients, setNewRecipients] = useState("");

  const addSub = useMutation({
    mutationFn: () => {
      const list = newRecipients.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
      if (list.length === 0) throw new Error("Add at least one recipient");
      return createSub({ data: {
        tenantId: tenantId!, customerId: customerId!,
        reportKey: newReport, cadence: newCadence, recipients: list,
      } });
    },
    onSuccess: () => {
      toast.success("Subscription created");
      setNewRecipients("");
      qc.invalidateQueries({ queryKey: ["portal-subs"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const toggleSub = useMutation({
    mutationFn: (v: { id: string; active: boolean }) => updateSub({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-subs"] }),
  });
  const removeSub = useMutation({
    mutationFn: (id: string) => delSub({ data: { id } }),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["portal-subs"] }); },
  });

  const reportLabel = (k: string) => REPORTS.find((r) => r.key === k)?.label ?? k;

  return (
    <>
      <PortalPageHeader title="Settings" description="Branding and scheduled report deliveries." />
      <div className="p-4 sm:p-6 space-y-6 max-w-4xl">
        <Card>
          <CardHeader><CardTitle>Brand Appearance</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Logo URL</Label>
                <Input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://..." />
              </div>
              <div className="space-y-1">
                <Label>Primary color</Label>
                <div className="flex items-center gap-2">
                  <Input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-16 h-10 p-1" />
                  <Input value={color} onChange={(e) => setColor(e.target.value)} placeholder="#6366f1" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Support email</Label>
                <Input type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} placeholder="ops@yourbrand.com" />
              </div>
              <div className="space-y-1">
                <Label>Welcome message</Label>
                <Textarea rows={2} value={welcome} onChange={(e) => setWelcome(e.target.value)} placeholder="Shown on the portal dashboard." />
              </div>
            </div>
            <div className="flex items-center gap-3">
              {logoUrl && <img src={logoUrl} alt="Logo preview" className="h-10 w-10 rounded object-contain border" />}
              <div className="h-6 w-6 rounded border" style={{ background: color }} />
              <span className="text-xs text-muted-foreground">Live preview</span>
              <Button className="ml-auto" onClick={() => saveBranding.mutate()} disabled={!customerId || saveBranding.isPending}>
                <Save className="h-4 w-4 mr-2" />Save branding
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Scheduled Reports</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Report</Label>
                <Select value={newReport} onValueChange={(v) => setNewReport(v as typeof newReport)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{REPORTS.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Cadence</Label>
                <Select value={newCadence} onValueChange={(v) => setNewCadence(v as typeof newCadence)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CADENCES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs text-muted-foreground">Recipients (comma-separated)</Label>
                <Input value={newRecipients} onChange={(e) => setNewRecipients(e.target.value)} placeholder="ops@brand.com, founder@brand.com" />
              </div>
              <Button className="sm:col-span-4" onClick={() => addSub.mutate()} disabled={!tenantId || !customerId || addSub.isPending}>
                <Plus className="h-4 w-4 mr-2" />Add subscription
              </Button>
            </div>

            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Report</TableHead>
                    <TableHead>Cadence</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead>Next run</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(subs?.subscriptions ?? []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground"><Mail className="h-4 w-4 inline mr-2" />No subscriptions yet.</TableCell></TableRow>}
                  {(subs?.subscriptions ?? []).map((s: any) => (
                    <TableRow key={s.id}>
                      <TableCell className="text-sm">{reportLabel(s.report_key)}</TableCell>
                      <TableCell><Badge variant="secondary">{s.cadence}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate max-w-xs">{(s.recipients ?? []).join(", ")}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{new Date(s.next_run_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <Switch checked={s.active} onCheckedChange={(v) => toggleSub.mutate({ id: s.id, active: v })} />
                      </TableCell>
                      <TableCell>
                        <Button size="icon" variant="ghost" onClick={() => removeSub.mutate(s.id)} disabled={removeSub.isPending}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}